import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import { EntityLink } from "scmp-app/components/entity-link";
import { useMyDailyFiveArticle } from "scmp-app/components/my-news/my-daily-five/hooks";
import type { primaryArticleContent$key } from "scmp-app/queries/__generated__/primaryArticleContent.graphql";

import {
  ActionBar,
  Container,
  ContentWrapper,
  CoverContainer,
  Headline,
  StyledEntityLink,
  StyledNumber,
  Summary,
  TopicLink,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  onClick?: (entityId?: string) => void;
  reference: primaryArticleContent$key;
};

export const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment primaryArticleContent on Content {
        entityId
        ...entityLink
        ...hooksUseMyDailyFiveContent
        ...hooksContentItemProvidersContent
          @arguments(
            withTopicLink: true
            withHeadline: true
            withSummary: true
            withCoverImage: true
            withCoverImageSize1200x800: true
            withCoverImageSize540x360: true
            withCoverImageSize640x360: true
            withActionBar: true
          )
        entityId
      }
    `,
    reference,
  );

  const isTrendingVisible = useMyDailyFiveArticle(content);
  const providers = useContentItemProviders(content, { type: "main" });

  return (
    <Container className={className} ref={ref}>
      <StyledNumber number={1} />
      <Wrapper>
        <ContentWrapper>
          <TopicLink>{providers.topicLink?.()}</TopicLink>
          <EntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
            <Headline>{providers.headline({ preferSocialHeadline: true })}</Headline>
          </EntityLink>
          <Summary>{providers.summary()}</Summary>
          <ActionBar>
            {providers.actionBar({
              isIconVisible: false,
              isTrendingVisible,
            })}
          </ActionBar>
        </ContentWrapper>
        <CoverContainer>
          <StyledEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "size1200x800",
                mobileUp: "size640x360",
                tabletUp: "size540x360",
              },
            })}
          </StyledEntityLink>
        </CoverContainer>
      </Wrapper>
    </Container>
  );
};

Component.displayName = "PrimaryArticle";

export const PrimaryArticle = withHighlightedHeadline(Component);
